import { z } from "zod";
import { zGuard } from "../zod/utils.js";

import { TimezoneName } from "../dateTypes.js";
import {
  DatetimeColumnDisplayFormat,
  NumberColumnDisplayFormat,
} from "../display-table/columnDisplayFormatTypes.js";
import { ExploreCustomTimeUnit } from "../explore/exploreCustomTimeUnit.js";
import {
  ExploreLumpOrderDirectionLiteral,
  ExploreLumpWindowFunctionLiteral,
  ExploreRankPredicate,
} from "../explore/exploreLumpTypes.js";
import { getNormalEnum } from "../pseudoEnums.js";
import { NoCodeCellDataframe } from "../sql/dataSourceTableConfig.js";

export const ChartTypeSelectorOptionLiteral = z.enum([
  "column_grouped",
  "column_stacked",
  "column_stacked100",
  "bar_grouped",
  "bar_stacked",
  "bar_stacked100",
  "histogram",
  "line",
  "scatter",
  "area_stacked",
  "area_stacked100",
  "pie",
]);
export type ChartTypeSelectorOption = z.infer<
  typeof ChartTypeSelectorOptionLiteral
>;
export const ChartTypeSelectorOption = getNormalEnum(
  ChartTypeSelectorOptionLiteral,
);

export const ChartColorHex = z.string();
export type ChartColorHex = z.infer<typeof ChartColorHex>;

export const ChartSortByValue = z.enum(["y", "-y", "x", "-x"]);
export type ChartSortByValue = z.infer<typeof ChartSortByValue>;

export const ChartSortDirectionLiteral = z.enum(["ascending", "descending"]);
export const ChartSortDirection = getNormalEnum(ChartSortDirectionLiteral);
export type ChartSortDirection = z.infer<typeof ChartSortDirectionLiteral>;

// NB: we need to flatten the union to use `getNormalEnum` below
export const ChartSortLiteral = z.enum([
  ...ChartSortByValue.options,
  ...ChartSortDirectionLiteral.options,
]);
export const ChartSort = getNormalEnum(ChartSortLiteral);
export type ChartSort = z.infer<typeof ChartSortLiteral>;

export const ChartCustomSort = z.array(z.string());
export type ChartCustomSort = z.infer<typeof ChartCustomSort>;

export const ChartSortOrCustomSort = z.union([
  ChartSortLiteral,
  ChartCustomSort,
]);
export type ChartSortOrCustomSort = z.infer<typeof ChartSortOrCustomSort>;

export const ChartTimeUnitLiteral = z.enum([
  "year",
  "quarter",
  "month",
  "week",
  "day",
  "dayofyear",
  "date",
  "hours",
  "minutes",
  "seconds",
  "milliseconds",
  "yearquarter",
  "yearquartermonth",
  "yearmonth",
  "yearmonthdate",
  "yearmonthdatehours",
  "yearmonthdatehoursminutes",
  "yearmonthdatehoursminutesseconds",
  "yearweek",
  "yearweekday",
  "yearweekdayhours",
  "yearweekdayhoursminutes",
  "yearweekdayhoursminutesseconds",
  "yeardayofyear",
  "quartermonth",
  "monthdate",
  "monthdatehours",
  "monthdatehoursminutes",
  "monthdatehoursminutesseconds",
  "weekday",
  "weekdayhours",
  "weekdayhoursminutes",
  "weekdayhoursminutesseconds",
  "dayhours",
  "dayhoursminutes",
  "dayhoursminutesseconds",
  "hoursminutes",
  "hoursminutesseconds",
  "minutesseconds",
  "secondsmilliseconds",
]);
export type ChartStandardTimeUnit = z.infer<typeof ChartTimeUnitLiteral>;
export const ChartStandardTimeUnit = getNormalEnum(ChartTimeUnitLiteral);

export const ChartTimeUnit = z.union([
  ChartTimeUnitLiteral,
  ExploreCustomTimeUnit,
]);
export type ChartTimeUnit = z.infer<typeof ChartTimeUnit>;

export const ChartWeekStartLiteral = z.enum(["sunday", "monday"]);
export const ChartWeekStart = getNormalEnum(ChartWeekStartLiteral);
export type ChartWeekStart = z.infer<typeof ChartWeekStartLiteral>;

export const ChartTimeUnitConfig = z.object({
  weekStart: ChartWeekStartLiteral.optional(),
});
export type ChartTimeUnitConfig = z.infer<typeof ChartTimeUnitConfig>;

/**
 * Use this when you have a timeUnit object that can contain a custom time unit,
 * and you only care about the default time units (e.g. passing them into vega)
 */
export const maybeGetStandardChartTimeUnit = (
  timeUnit: ChartTimeUnit | undefined,
): ChartStandardTimeUnit | undefined => {
  if (zGuard(ExploreCustomTimeUnit, timeUnit)) {
    return undefined;
  }
  return timeUnit;
};
export const ChartAggregateLiteral = z.enum([
  "count",
  "sum",
  "average",
  "distinct",
  "median",
  "max",
  "min",
  "ci0",
  "ci1",
  "mean",
  "missing",
  "product",
  "q1",
  "q3",
  "stderr",
  "stdev",
  "stdevp",
  "valid",
  "values",
  "variance",
  "variancep",
  "preagg",
]);
export type ChartAggregate = z.infer<typeof ChartAggregateLiteral>;
export const ChartAggregate = getNormalEnum(ChartAggregateLiteral);

export const ChartAxisLabels = z.object({
  angle: z.number().optional(),
  maxTickLabelLimit: z.number().optional(),
});
export type ChartAxisLabels = z.infer<typeof ChartAxisLabels>;

export const ChartAxisTicks = z.object({
  size: z.number().optional(),
  count: z.number().optional(),
  minStep: z.number().optional(),
});
export type ChartAxisTicks = z.infer<typeof ChartAxisTicks>;

export const ChartGridStyle = z.enum(["solid", "dash", "none"]);
export type ChartGridStyle = z.infer<typeof ChartGridStyle>;
export const zChartGridStyle = z.enum(["solid", "dash", "none"]);

export const ChartAxisGrid = z.object({
  style: ChartGridStyle,
  width: z.number().optional(),
});
export type ChartAxisGrid = z.infer<typeof ChartAxisGrid>;

export const ChartNumberAxisScale = z.enum(["linear", "pow", "sqrt", "log"]);
export type ChartNumberAxisScale = z.infer<typeof ChartNumberAxisScale>;
export const zChartNumberAxisScale = z.enum(["linear", "pow", "sqrt", "log"]);

export const ChartAxisStyle = z.object({
  position: z.enum(["top", "bottom", "left", "right"]).optional(),
  labels: ChartAxisLabels.optional(),
  grid: ChartAxisGrid.optional(),
  ticks: ChartAxisTicks.optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  zero: z.boolean().optional(),
});
export type ChartAxisStyle = z.infer<typeof ChartAxisStyle>;

export const ChartAxisReferenceLineLabelPosition = z.enum([
  "outside",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
]);
export type ChartAxisReferenceLineLabelPosition = z.infer<
  typeof ChartAxisReferenceLineLabelPosition
>;

const ChartDataTypeString = z.literal("string");
const ChartDataTypeNumber = z.literal("number");
const ChartDataTypeDatetime = z.literal("datetime");
export const ChartDataTypeLiteral = z.union([
  ChartDataTypeString,
  ChartDataTypeNumber,
  ChartDataTypeDatetime,
]);
export type ChartDataType = z.infer<typeof ChartDataTypeLiteral>;
export const ChartDataType = getNormalEnum(ChartDataTypeLiteral);

export const ChartAxisReferenceLineDataType = z.union([
  ChartDataTypeNumber,
  ChartDataTypeDatetime,
  ChartDataTypeString,
]);
export type ChartAxisReferenceLineDataType = z.infer<
  typeof ChartAxisReferenceLineDataType
>;

export const ChartLineStroke = z.enum(["solid", "dashed", "dotted"]);
export type ChartLineStroke = z.infer<typeof ChartLineStroke>;
export const zChartLineStroke = z.enum(["solid", "dashed", "dotted"]);

export const ChartAxisReferenceLine = z.object({
  id: z.string(),
  label: z.string().optional(),
  labelPosition: ChartAxisReferenceLineLabelPosition.optional(),
  labelAngle: z.number().optional(),
  strokeColor: ChartColorHex.optional(),
  strokeWidth: z.number().optional(),
  strokeStyle: ChartLineStroke.optional(),
  value: z.union([z.number(), z.string()]).optional(),
  dataType: ChartAxisReferenceLineDataType,
});
export type ChartAxisReferenceLine = z.infer<typeof ChartAxisReferenceLine>;

export const ChartLump = z.object({
  windowFunction: ExploreLumpWindowFunctionLiteral,
  orderBy: z.string().optional(),
  orderDirection: ExploreLumpOrderDirectionLiteral.optional(),
  predicate: ExploreRankPredicate,
  otherLabel: z.string().optional(),
});
export type ChartLump = z.infer<typeof ChartLump>;

export const BaseChartAxis = z.object({
  type: ChartDataTypeLiteral,
  title: z.union([z.string(), z.literal(null)]).optional(),
  aggregate: ChartAggregateLiteral.optional(),
  style: ChartAxisStyle,
  numberFormat: NumberColumnDisplayFormat.optional(),
  datetimeFormat: DatetimeColumnDisplayFormat.optional(),
  scale: ChartNumberAxisScale.optional(),
  sort: ChartSortLiteral.optional(),
  lump: ChartLump.optional(),
  timeUnit: ChartTimeUnit.optional(),
  timeUnitConfig: ChartTimeUnitConfig.optional(),
  referenceLines: z.array(ChartAxisReferenceLine).optional(),
  hideTitle: z.boolean().optional(),
});

export const ChartStringAxis = BaseChartAxis.extend({
  type: ChartDataTypeString,
});
export type ChartStringAxis = z.infer<typeof ChartStringAxis>;

export const ChartDatetimeAxis = BaseChartAxis.extend({
  type: ChartDataTypeDatetime,
});
export type ChartDatetimeAxis = z.infer<typeof ChartDatetimeAxis>;

export const ChartNumberAxis = BaseChartAxis.extend({
  type: ChartDataTypeNumber,
});
export type ChartNumberAxis = z.infer<typeof ChartNumberAxis>;

export const ChartAxis = z.union([
  ChartStringAxis,
  ChartNumberAxis,
  ChartDatetimeAxis,
]);
export type ChartAxis = z.infer<typeof ChartAxis>;

const ChartAxisDataFrame = {
  dataFrameColumn: z.string().optional(),
};
export const ChartStringAxisWithData = ChartStringAxis.omit({
  sort: true,
}).extend({
  ...ChartAxisDataFrame,
  // support custom sort for categorical x-axis via an array of x-axis values
  sort: ChartSortOrCustomSort.optional(),
});
export const ChartNumberAxisWithData =
  ChartNumberAxis.extend(ChartAxisDataFrame);
export const ChartDatetimeAxisWithData =
  ChartDatetimeAxis.extend(ChartAxisDataFrame);
export const ChartAxisWithData = z.union([
  ChartStringAxisWithData,
  ChartNumberAxisWithData,
  ChartDatetimeAxisWithData,
]);
export type ChartAxisWithData = z.infer<typeof ChartAxisWithData>;

export const ChartTooltipDisplayFormat = z.union([
  NumberColumnDisplayFormat,
  DatetimeColumnDisplayFormat,
]);
export type ChartTooltipDisplayFormat = z.infer<
  typeof ChartTooltipDisplayFormat
>;

export const ChartTooltipField = z.object({
  id: z.string().optional(),
  dataFrameColumn: z.string(),
  name: z.string().optional(),
  type: ChartDataTypeLiteral.optional(),
  aggregate: ChartAggregateLiteral.optional(),
  displayFormat: ChartTooltipDisplayFormat.optional(),
});
export type ChartTooltipField = z.infer<typeof ChartTooltipField>;

export const ChartAutoTooltip = z.object({
  type: z.literal("auto"),
});
export type ChartAutoTooltip = z.infer<typeof ChartAutoTooltip>;

// legacy Chart Cell semantics for manual tooltip
// we infer all sorts of stuff around aggregate/format/timeunit
export const ChartManualTooltip = z.object({
  type: z.literal("manual"),
  includeAuto: z.boolean().optional(),
  fields: z.array(ChartTooltipField),
});
export type ChartManualTooltip = z.infer<typeof ChartManualTooltip>;

// new Explore Cell semantics for custom tooltips
// everything is up to the user
export const ChartCustomTooltip = z.object({
  type: z.literal("custom"),
  includeAuto: z.boolean(),
  fields: z.array(ChartTooltipField),
});
export type ChartCustomTooltip = z.infer<typeof ChartCustomTooltip>;

export const ChartTooltip = z.union([
  ChartAutoTooltip,
  ChartManualTooltip,
  ChartCustomTooltip,
]);
export type ChartTooltip = z.infer<typeof ChartTooltip>;

export const ChartColorScheme = z.enum([
  "greys",
  "blues",
  "reds",
  "greens",
  "viridis",
  "inferno",
  "plasma",
  "cividis",
  "spectral",
  "redblue",
  "purplegreen",
  "redyellowgreen",
]);
export type ChartColorScheme = z.infer<typeof ChartColorScheme>;
const zChartColorSchemeSequentialSingleHue = z
  .enum(["greys", "blues", "reds", "greens"])
  .describe(
    "Sequential color scheme with a single hue, designed to encode increasing numeric values.",
  );
const zChartColorSchemeSequentialMultiHue = z
  .enum(["viridis", "inferno", "plasma", "cividis"])
  .describe(
    "Sequential color scheme with multiple hues, designed to encode increasing numeric values while using additional hues for more color discrimination.",
  );
const zChartColorSchemeDiverging = z
  .enum(["spectral", "redblue", "purplegreen", "redyellowgreen"])
  .describe(
    "Diverging color scheme, designed to encode values with a meaningful midpoint.",
  );
export const zChartColorScheme = z.union([
  zChartColorSchemeSequentialSingleHue,
  zChartColorSchemeSequentialMultiHue,
  zChartColorSchemeDiverging,
]);

export const ChartColorMappings = z.record(z.string(), ChartColorHex);
export type ChartColorMappings = z.infer<typeof ChartColorMappings>;

// color variations:
// type = "static" -> color/defaultColor(?)
// type = "dataframe" aka continuous -> dataFrameColumn + optional aggregate + dataType (as input to agg?)
// type = "series" aka categorical -> dataType=string + ../{colorDataFrameColumn + colorOrder}
// type = "series" - ../colorDataFrameColumn + colorsBySeriesValues aka "color by bar" -> dataType=string

export const BaseChartColor = z.object({
  aggregate: ChartAggregateLiteral.optional(),
  scheme: ChartColorScheme.optional(),
  reverse: z.boolean().optional(),
  style: ChartAxisStyle.optional(),
  scale: ChartNumberAxisScale.optional(),
  numberFormat: NumberColumnDisplayFormat.optional(),
  datetimeFormat: DatetimeColumnDisplayFormat.optional(),
  dataFrameColumn: z.string().optional(),
  color: ChartColorHex.optional(),
  dataType: ChartDataTypeLiteral.optional(),
  defaultColor: ChartColorHex.optional(),
  colorsBySeriesValues: ChartColorMappings.optional(),
});
export type BaseChartColor = z.infer<typeof BaseChartColor>;

export const ChartColorStatic = BaseChartColor.extend({
  type: z.literal("static"),
});
export type ChartColorStatic = z.infer<typeof ChartColorStatic>;

export const ChartColorDataframe = BaseChartColor.extend({
  type: z.literal("dataframe"),
  dataType: ChartDataTypeLiteral,
});
export type ChartColorDataframe = z.infer<typeof ChartColorDataframe>;

export const ChartColorSeriesValue = BaseChartColor.extend({
  type: z.literal("series"),
  colorsBySeriesValues: ChartColorMappings,
  lump: ChartLump.optional(),
});
export type ChartColorSeriesValue = z.infer<typeof ChartColorSeriesValue>;

export const ChartColor = z.union([
  ChartColorStatic,
  ChartColorDataframe,
  ChartColorSeriesValue,
]);
export type ChartColor = z.infer<typeof ChartColor>;

export const ChartOpacityStatic = z.object({
  type: z.literal("static"),
  value: z.number(),
});
export type ChartOpacityStatic = z.infer<typeof ChartOpacityStatic>;

export const ChartOpacityDataframe = z.object({
  type: z.literal("dataframe"),
  dataFrameColumn: z.string().optional(),
  dataType: ChartDataTypeLiteral,
});
export type ChartOpacityDataframe = z.infer<typeof ChartOpacityDataframe>;

export const ChartOpacitiesBySeriesValues = z.record(z.string(), z.number());
export const ChartOpacitySeriesValue = z.object({
  type: z.literal("series"),
  opacitiesBySeriesValues: ChartOpacitiesBySeriesValues,
});
export type ChartOpacitySeriesValue = z.infer<typeof ChartOpacitySeriesValue>;

export const ChartOpacity = z.union([
  ChartOpacityStatic,
  ChartOpacityDataframe,
  ChartOpacitySeriesValue,
]);
export type ChartOpacity = z.infer<typeof ChartOpacity>;

export const ChartDataLabelPointPosition = z.enum([
  "auto",
  "above",
  "above-left",
  "above-right",
  "below",
  "below-left",
  "below-right",
  "left",
  "right",
  "center",
]);
const zChartDataLabelPointPosition = z
  .enum([
    "auto",
    "above",
    "above-left",
    "above-right",
    "below",
    "below-left",
    "below-right",
    "left",
    "right",
    "center",
  ])
  .describe(
    `Where the data label is rendered with respect to the point mark in line/area/scatter charts.

    The default position is 'auto' which avoids overlap by moving or hiding while the other positions show one label per point at the given position.`,
  );

export const ChartDataLabelBarPosition = z.enum([
  "inside-end",
  "inside-center",
  "outside-end",
]);
const zChartDataLabelBarPosition = z
  .enum(["inside-end", "inside-center", "outside-end"])
  .describe(
    `Where the data label is rendered with respect to the bar mark in bar/column/histogram charts or the pie slice in pie charts.

    Total data labels are always rendered at the 'outside-end' position.

    Per color data labels default to 'inside-end' position for bar marks and 'inside-center' position for pie slices.

    'inside-end' position is not allowed for pie slices.`,
  );

export const ChartDataLabelPosition = z.union([
  ChartDataLabelPointPosition,
  ChartDataLabelBarPosition,
]);
export type ChartDataLabelPosition = z.infer<typeof ChartDataLabelPosition>;
export const zChartDataLabelPosition = z.union([
  zChartDataLabelPointPosition,
  zChartDataLabelBarPosition,
]);

export const ChartDataLabels = z.object({
  position: ChartDataLabelPosition.optional(),
  angle: z.number().optional(),
  color: ChartColorHex.optional(),
  fontSize: z.number().optional(),
});
export type ChartDataLabels = z.infer<typeof ChartDataLabels>;

export const ChartShape = z.enum([
  "circle",
  "square",
  "cross",
  "diamond",
  "triangle-up",
  "triangle-down",
  "triangle-right",
  "triangle-left",
  "tick-vertical",
  "tick-horizontal",
  "stroke",
  "arrow",
  "wedge",
  "triangle",
]);
export type ChartShape = z.infer<typeof ChartShape>;
export const zChartShape = z
  .enum([
    "circle",
    "square",
    "cross",
    "diamond",
    "triangle-up",
    "triangle-down",
    "triangle-right",
    "triangle-left",
    "tick-vertical",
    "tick-horizontal",
    "stroke",
    "arrow",
    "wedge",
    "triangle",
  ])
  .describe("The symbol used to render point marks in scatter charts.");

export const ChartBinType = z.enum(["count", "size", "dataFrameColumn"]);
export type ChartBinType = z.infer<typeof ChartBinType>;

export const ChartBin = z.object({
  type: ChartBinType,
  value: z.number().optional(),
});
export type ChartBin = z.infer<typeof ChartBin>;

export const ChartHistogramFormat = z.enum(["count", "percentage"]);
export type ChartHistogramFormat = z.infer<typeof ChartHistogramFormat>;

export const ChartSeriesBarType = z.literal("bar");
export type ChartSeriesBarType = z.infer<typeof ChartSeriesBarType>;

export const ChartSeriesLineType = z.literal("line");
export type ChartSeriesLineType = z.infer<typeof ChartSeriesLineType>;

export const ChartSeriesScatterType = z.literal("scatter");
export type ChartSeriesScatterType = z.infer<typeof ChartSeriesScatterType>;

export const ChartSeriesAreaType = z.literal("area");
export type ChartSeriesAreaType = z.infer<typeof ChartSeriesAreaType>;

export const ChartSeriesHistogramType = z.literal("histogram");
export type ChartSeriesHistogramType = z.infer<typeof ChartSeriesHistogramType>;

export const ChartSeriesPieType = z.literal("pie");
export type ChartSeriesPieType = z.infer<typeof ChartSeriesPieType>;

export const ChartSeriesTypeLiteral = z.union([
  ChartSeriesBarType,
  ChartSeriesLineType,
  ChartSeriesScatterType,
  ChartSeriesAreaType,
  ChartSeriesHistogramType,
  ChartSeriesPieType,
]);
export const ChartSeriesType = getNormalEnum(ChartSeriesTypeLiteral);
export type ChartSeriesType = z.infer<typeof ChartSeriesTypeLiteral>;

export const BaseChartSeries = z.object({
  id: z.string(),
  name: z.string().optional(),
  type: ChartSeriesTypeLiteral,
  axis: ChartAxis,
  dataFrame: z.string().optional(),
  dataFrameColumns: z.array(z.string()),
  exploreOnlyWideInputAggregations: z
    .array(z.union([ChartAggregateLiteral, z.literal(null)]))
    .optional(),
  colorDataFrameColumn: z.string().optional(),
  colorOrder: ChartSortOrCustomSort,
  color: ChartColor,
  opacity: ChartOpacity,
  tooltip: ChartTooltip.optional(),
  dataLabels: ChartDataLabels.optional(),
  totalDataLabels: ChartDataLabels.optional(),
  legendTitle: z.union([z.string(), z.literal(null)]).optional(),
  hideLegendTitle: z.boolean().optional(),
});
export type BaseChartSeries = z.infer<typeof BaseChartSeries>;

export const ChartInterpolate = z.enum([
  "linear",
  "monotone",
  "step-before",
  "step-after",
  "step",
  "basis",
  "cardinal",
]);
export type ChartInterpolate = z.infer<typeof ChartInterpolate>;

export const ChartLineSeries = BaseChartSeries.extend({
  type: ChartSeriesLineType,
  point: z.boolean(),
  interpolate: ChartInterpolate.optional(),
  stroke: ChartLineStroke.optional(),
  width: z.number().optional(),
});
export type ChartLineSeries = z.infer<typeof ChartLineSeries>;

export const ChartBarLayout = z.enum(["grouped", "stacked", "stacked100"]);
export type ChartBarLayout = z.infer<typeof ChartBarLayout>;

export const ChartBarSeries = BaseChartSeries.extend({
  type: ChartSeriesBarType,
  orientation: z.enum(["horizontal", "vertical"]),
  barWidth: z.number(),
  layout: ChartBarLayout,
});
export type ChartBarSeries = z.infer<typeof ChartBarSeries>;

export const ChartScatterSeries = BaseChartSeries.extend({
  type: ChartSeriesScatterType,
  filled: z.boolean(),
  shape: ChartShape.optional(),
  size: z.number().optional(),
});
export type ChartScatterSeries = z.infer<typeof ChartScatterSeries>;

export const ChartAreaSeries = BaseChartSeries.extend({
  type: ChartSeriesAreaType,
  line: z.boolean(),
  point: z.boolean(),
  interpolate: ChartInterpolate.optional(),
  normalize: z.boolean(),
});
export type ChartAreaSeries = z.infer<typeof ChartAreaSeries>;

export const ChartHistogramSeries = BaseChartSeries.extend({
  type: ChartSeriesHistogramType,
  bin: ChartBin,
  format: ChartHistogramFormat,
});
export type ChartHistogramSeries = z.infer<typeof ChartHistogramSeries>;

export const ChartPieSeries = BaseChartSeries.extend({
  type: ChartSeriesPieType,
  radius: z.number(),
  showAsPct: z.boolean().optional(),
});
export type ChartPieSeries = z.infer<typeof ChartPieSeries>;

export const ChartSeries = z.union([
  ChartLineSeries,
  ChartBarSeries,
  ChartScatterSeries,
  ChartAreaSeries,
  ChartHistogramSeries,
  ChartPieSeries,
]);
export type ChartSeries = z.infer<typeof ChartSeries>;

export const ChartSeriesGroup = z.array(z.string());
export type ChartSeriesGroup = z.infer<typeof ChartSeriesGroup>;

export const ChartFacetDimension = z.object({
  type: ChartDataTypeLiteral,
  dataFrameColumn: z.string().optional(),
  title: z.union([z.string(), z.literal(null)]).optional(),
  sort: ChartSortOrCustomSort.optional(),
  maxBins: z.number().optional(),
  binStep: z.number().optional(), //note: there's no UI for this yet?
  timeUnit: ChartTimeUnit.optional(),
  timeUnitConfig: ChartTimeUnitConfig.optional(),
  hideTitle: z.boolean().optional(),
  lump: ChartLump.optional(),
});
export type ChartFacetDimension = z.infer<typeof ChartFacetDimension>;

export const ChartFacet = z.object({
  columns: z.number().optional(),
  sharedY: z.boolean().optional(),
  facetHorizontal: ChartFacetDimension.optional(),
  facetVertical: ChartFacetDimension.optional(),
});
export type ChartFacet = z.infer<typeof ChartFacet>;

export const ChartLayer = z.object({
  id: z.string(),
  dataFrame: NoCodeCellDataframe.optional(),
  xAxis: ChartAxisWithData,
  series: z.array(ChartSeries),
  seriesGroups: z.array(ChartSeriesGroup).optional(),
});
export type ChartLayer = z.infer<typeof ChartLayer>;

export const ChartLegendOrientLiteral = z.enum([
  "none",
  "left",
  "right",
  "top",
  "bottom",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
]);
export const ChartLegendOrient = getNormalEnum(ChartLegendOrientLiteral);
export type ChartLegendOrient = z.infer<typeof ChartLegendOrientLiteral>;

export const ChartFont = z.object({
  baseSize: z.number(),
});
export type ChartFont = z.infer<typeof ChartFont>;

export const ChartLegend = z.object({
  position: ChartLegendOrientLiteral,
});
export type ChartLegend = z.infer<typeof ChartLegend>;

export const ChartType = z.enum(["layered", "concat"]);
export type ChartType = z.infer<typeof ChartType>;

// Annotated so declaration emit references `ChartLayer` by name instead of
// inlining its whole structure, which exceeds the serializer's size limit.
export const ConcatChart: z.ZodObject<{
  id: z.ZodString;
  layers: z.ZodArray<typeof ChartLayer>;
}> = z.object({
  id: z.string(),
  layers: z.array(ChartLayer),
});
export type ConcatChart = z.infer<typeof ConcatChart>;

export const ChartSize = z.enum(["auto", "container"]);
export type ChartSize = z.infer<typeof ChartSize>;

export const ChartGeneralSettings = z.object({
  legend: ChartLegend,
  font: ChartFont.optional(),
  tooltip: z.boolean(),
  timezone: TimezoneName.optional(),
  selectionEnabled: z.boolean().optional(), // Optional for backward compatibility
  baseAxisSize: ChartSize.optional(), // defaults to "container"
});
export type ChartGeneralSettings = z.infer<typeof ChartGeneralSettings>;

const BaseChartSpec = z.object({
  type: ChartType,
  settings: ChartGeneralSettings,
});

export const LayeredChartSpec: z.ZodObject<
  Omit<(typeof BaseChartSpec)["shape"], "type"> & {
    type: z.ZodLiteral<"layered">;
    layers: z.ZodArray<typeof ChartLayer>;
    facet: z.ZodOptional<typeof ChartFacet>;
  }
> = BaseChartSpec.extend({
  type: z.literal("layered"),
  layers: z.array(ChartLayer),
  facet: ChartFacet.optional(),
});
export type LayeredChartSpec = z.infer<typeof LayeredChartSpec>;

export const ConcatChartSpec = BaseChartSpec.extend({
  type: z.literal("concat"),
  columns: z.number(),
  charts: z.array(ConcatChart),
});
export type ConcatChartSpec = z.infer<typeof ConcatChartSpec>;
export const UnsupportedChartSpec = z.object({
  type: z.literal("unsupported"),
});
export type UnsupportedChartSpec = z.infer<typeof UnsupportedChartSpec>;

export const ChartSpec: z.ZodUnion<
  [typeof LayeredChartSpec, typeof ConcatChartSpec, typeof UnsupportedChartSpec]
> = z.union([LayeredChartSpec, ConcatChartSpec, UnsupportedChartSpec]);
export type ChartSpec = z.infer<typeof ChartSpec>;

export const VegaIntervalNumericRange = z.tuple([z.number(), z.number()]);
export type VegaIntervalNumericRange = z.infer<typeof VegaIntervalNumericRange>;

export const VegaIntervalDatetimeRange = z.tuple([z.string(), z.string()]);
export type VegaIntervalDatetimeRange = z.infer<
  typeof VegaIntervalDatetimeRange
>;

export const VegaIntervalRange = z.union([
  VegaIntervalNumericRange,
  VegaIntervalDatetimeRange,
]);
export type VegaIntervalRange = z.infer<typeof VegaIntervalRange>;

export const VegaIntervalStringCategories = z.array(z.string());
export type VegaIntervalStringCategories = z.infer<
  typeof VegaIntervalStringCategories
>;

export const VegaIntervalNumericCategories = z.array(z.number());
export type VegaIntervalNumericCategories = z.infer<
  typeof VegaIntervalNumericCategories
>;

export const VegaIntervalBooleanCategories = z.array(z.boolean());
export type VegaIntervalBooleanCategories = z.infer<
  typeof VegaIntervalBooleanCategories
>;

export const VegaIntervalCategories = z.union([
  VegaIntervalStringCategories,
  VegaIntervalNumericCategories,
  VegaIntervalBooleanCategories,
]);
export type VegaIntervalCategories = z.infer<typeof VegaIntervalCategories>;

export const VegaIntervalSelection = z.record(
  z.string(),
  z.union([VegaIntervalRange, VegaIntervalCategories]),
);
export type VegaIntervalSelection = z.infer<typeof VegaIntervalSelection>;

export const VegaPointSelectionScalar = z.union([
  z.string(),
  z.number(),
  z.boolean(),
]);
export type VegaPointSelectionScalar = z.infer<typeof VegaPointSelectionScalar>;

export const VegaPointSelectionDatum = z.record(
  z.string(),
  VegaPointSelectionScalar,
);
export type VegaPointSelectionDatum = z.infer<typeof VegaPointSelectionDatum>;

export const VegaPointSelection = z.array(VegaPointSelectionDatum);
export type VegaPointSelection = z.infer<typeof VegaPointSelection>;

export const VegaSelection = z.union([
  VegaIntervalSelection,
  VegaPointSelection,
]);
export type VegaSelection = z.infer<typeof VegaSelection>;

export const ChartSelection = z.record(z.string(), VegaSelection);
export type ChartSelection = z.infer<typeof ChartSelection>;

export const VegaSelectionType = z.enum(["point", "interval"]);
export type VegaSelectionType = z.infer<typeof VegaSelectionType>;

export const DerivedFieldType = z.enum(["rename", "bin", "timeunit"]);
export type DerivedFieldType = z.infer<typeof DerivedFieldType>;

export const BaseDerivedField = z.object({
  type: DerivedFieldType,
  derivedName: z.string(),
  sourceName: z.string(),
});
export type BaseDerivedField = z.infer<typeof BaseDerivedField>;

export const RenamedField = BaseDerivedField.extend({
  type: z.literal("rename"),
});
export type RenamedField = z.infer<typeof RenamedField>;

export const BinsSignal = z.object({
  name: z.string(),
  scope: z.array(z.number()),
});
export type BinsSignal = z.infer<typeof BinsSignal>;

export const BinsConfig = z.object({
  start: z.number(),
  step: z.number(),
  stop: z.number(),
});
export type BinsConfig = z.infer<typeof BinsConfig>;

export const NumericBinnedField = BaseDerivedField.extend({
  type: z.literal("bin"),
  binsSignal: BinsSignal.optional(),
  binsConfig: BinsConfig.optional(),
});
export type NumericBinnedField = z.infer<typeof NumericBinnedField>;

export const TimeunitBinnedField = BaseDerivedField.extend({
  type: z.literal("timeunit"),
  timeUnit: ChartTimeUnitLiteral,
  timeUnitConfig: ChartTimeUnitConfig.optional(),
});
export type TimeunitBinnedField = z.infer<typeof TimeunitBinnedField>;

export const SelectionDerived = z.array(
  z.union([RenamedField, NumericBinnedField, TimeunitBinnedField]),
);
export type SelectionDerived = z.infer<typeof SelectionDerived>;

export const VegaSelectionConfig = z.object({
  type: VegaSelectionType,
  datetimeFields: z.array(z.string()),
  derived: SelectionDerived,
});
export type VegaSelectionConfig = z.infer<typeof VegaSelectionConfig>;

// For each Vega-Lite selection that the chart translator adds to the resulting
// Vega-Lite spec, a corresponding VegaSelectionConfig is created.
// The VegaSelectionConfigs are stored in the Vega-Lite spec's usermeta object,
// which gets passed through the Vega-Lite to Vega compilation process and through
// the VegaFusion pre-transform process. The VegaSelectionConfigs are used
// by our Vega renderer component to determine the callbacks that are needed,
// and to determine any data conversions that should be performed in the callbacks
// (in particular, which datetime fields should be converted into strings)
export const VegaSelectionConfigs = z.record(z.string(), VegaSelectionConfig);
export type VegaSelectionConfigs = z.infer<typeof VegaSelectionConfigs>;

export const ColumnKind = z.enum([
  "bool",
  "datetime",
  "float",
  "int",
  "string",
]);
export type ColumnKind = z.infer<typeof ColumnKind>;

export const ChartSqlColumnMappings = z.record(z.string(), z.string()); // new name -> old name
export type ChartSqlColumnMappings = z.infer<typeof ChartSqlColumnMappings>;
